import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, GraduationCap, Archive } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { isStaffUser } from "@/lib/constants";

const empty = { kurs_name: "", sakrament: "EK", jahr: new Date().getFullYear() + 1, monat: "", ort: "", kurs_gemeinde: "", kurs_beginn: "", kurs_ende: "", online_until: "" };

export default function CoursesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const staff = isStaffUser(user);

  const [params] = useSearchParams();
  const load = () => api.get("/courses").then(({ data }) => setCourses(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("neu") === "1" && staff) setOpen(true); }, [params, staff]);

  const withMonat = ["TA", "EH"].includes(form.sakrament);
  const suggestedName = () => {
    const mo = withMonat && form.monat ? `-${form.monat}` : "";
    return `${form.sakrament}-${form.jahr}${mo}${form.ort ? "-" + form.ort : ""}`;
  };

  const create = async () => {
    const payload = { ...form, jahr: Number(form.jahr), kurs_name: form.kurs_name || suggestedName(), online_until: form.online_until || null };
    try {
      const { data } = await api.post("/courses", payload);
      toast.success("Kurs angelegt.");
      setOpen(false); setForm(empty); load();
      window.dispatchEvent(new Event("courses-changed"));
      navigate(`/kurs/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const active = courses.filter((c) => !c.expired);
  const expired = courses.filter((c) => c.expired);
  const groups = {};
  active.forEach((c) => { (groups[c.group] = groups[c.group] || []).push(c); });

  const CourseCard = (c) => (
    <button key={c.id} onClick={() => navigate(`/kurs/${c.id}`)} className="text-left bg-white border border-border rounded-lg p-4 shadow-sm hover:border-primary transition-colors duration-200" data-testid={`course-card-${c.id}`}>
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap size={16} className="text-primary" />
        <span className="font-medium truncate">{c.kurs_name}</span>
      </div>
      <p className="text-sm text-muted-foreground truncate">{c.kurs_gemeinde}</p>
    </button>
  );

  return (
    <div className="px-8 py-10">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-serif">Kurse</h1>
        {staff && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-course-button"><Plus size={16} className="mr-1" /> Neuer Kurs</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Neuen Kurs anlegen</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Sakrament</Label>
                    <Select value={form.sakrament} onValueChange={(v) => setForm({ ...form, sakrament: v })}>
                      <SelectTrigger data-testid="course-sakrament"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TA">TA – Taufe</SelectItem>
                        <SelectItem value="EK">EK – Erstkommunion</SelectItem>
                        <SelectItem value="FI">FI – Firmung</SelectItem>
                        <SelectItem value="EH">EH – Eheschließung</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Jahr</Label><Input type="number" value={form.jahr} onChange={(e) => setForm({ ...form, jahr: e.target.value })} data-testid="course-jahr" /></div>
                </div>
                {withMonat && (
                  <div className="space-y-2">
                    <Label>Monat (01–12)</Label>
                    <Select value={form.monat} onValueChange={(v) => setForm({ ...form, monat: v })}>
                      <SelectTrigger data-testid="course-monat"><SelectValue placeholder="Monat wählen" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2"><Label>Ort (optional, z.B. Otterbach1)</Label><Input value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} data-testid="course-ort" /></div>
                <div className="space-y-2"><Label>Kurs-Name</Label><Input value={form.kurs_name} onChange={(e) => setForm({ ...form, kurs_name: e.target.value })} placeholder={suggestedName()} data-testid="course-name" /></div>
                <div className="space-y-2"><Label>Kurs-Gemeinde</Label><Input value={form.kurs_gemeinde} onChange={(e) => setForm({ ...form, kurs_gemeinde: e.target.value })} data-testid="course-gemeinde" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2"><Label>Kurs-Beginn</Label><Input type="date" value={form.kurs_beginn} onChange={(e) => setForm({ ...form, kurs_beginn: e.target.value })} data-testid="course-beginn" /></div>
                  <div className="space-y-2"><Label>Kurs-Ende</Label><Input type="date" value={form.kurs_ende} onChange={(e) => setForm({ ...form, kurs_ende: e.target.value })} data-testid="course-ende" /></div>
                </div>
                <div className="space-y-2"><Label>Online bis (optional)</Label><Input type="date" value={form.online_until} onChange={(e) => setForm({ ...form, online_until: e.target.value })} data-testid="course-online-until" /></div>
              </div>
              <DialogFooter><Button onClick={create} data-testid="save-course-button">Kurs anlegen</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {courses.length === 0 && <p className="text-muted-foreground text-sm">Noch keine Kurse.</p>}

      {Object.keys(groups).sort().map((g) => (
        <div key={g} className="mb-8">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 mb-3">{g}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{groups[g].map(CourseCard)}</div>
        </div>
      ))}

      {expired.length > 0 && (
        <div className="mb-8">
          <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-400 mb-3"><Archive size={16} /> Abgelaufene Kurse</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 opacity-70">{expired.map(CourseCard)}</div>
        </div>
      )}
    </div>
  );
}
